import { useId } from 'react'
import { Tooltip } from '@/components/ui'
import { cn } from '@/internationalization/cn'
import { formatTimecode, formatTimecodeOrDash } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import { usePlayer } from './PlayerContext'
import { activateTimecode, retriesOnActivate, timecodeAvailability } from './timecode'

const BASE = 'font-mono text-xs tabular-nums text-fg-subtle'

/**
 * A timecode that plays the recording from that moment.
 *
 * ONE RULE FOR "NOT READY". There were three versions of this control, each
 * with its own: plain text (Summary), disabled (Actions) and always-enabled
 * (transcript). This is the one for anywhere that is not the transcript.
 * `TranscriptLine` deliberately does not use it: it is memoised and takes
 * `onSeek` as a prop precisely so it does NOT subscribe to the player, and a
 * `usePlayer()` inside each of thousands of lines would re-render all of them
 * four times a second.
 *
 * IT SUBSCRIBES HERE, NOT IN ITS PARENT. The player publishes `currentTime`
 * every 250 ms while playing, and every context consumer re-renders with it.
 * Reading the player in a list component re-rendered every row's pickers and
 * editors at that rate; reading it in this one-button leaf costs one button.
 *
 * NOT `disabled`. A disabled button leaves the tab order, so a keyboard user
 * would not even learn there is a timecode that cannot play. It stays a
 * focusable native button with `aria-disabled`, and the click handler refuses
 * explicitly -- `aria-disabled` alone changes only what is announced, not what
 * happens on activation. Enter and Space fire `click` on a native button, so
 * no key handling is needed. The reason is a tooltip (opens on focus too) AND
 * a described-by span, because the tooltip is portalled and not linked to the
 * button for assistive technology.
 *
 * A null `seconds` renders the house em dash as text: a model-extracted item
 * can cite no moment, and `0:00` would claim it was said in the first second.
 * Zero is a real position, hence `== null` and never a truthiness check.
 */
export function PlayerTimecode({
  seconds,
  onUnavailable,
  className,
}: {
  seconds: number | null
  /** Called with the moment when playback is impossible (not while loading). */
  onUnavailable?: (seconds: number) => void
  className?: string
}) {
  const t = useT()
  const player = usePlayer()
  const reasonId = useId()

  if (seconds == null) {
    return <span className={cn(BASE, className)}>{formatTimecodeOrDash(seconds)}</span>
  }

  const availability = timecodeAvailability(player)
  const time = formatTimecode(seconds)

  // Audio cannot play but a click still goes somewhere useful (the transcript),
  // so the control must not look or announce itself as disabled. Loading never
  // takes the fallback (see `activateTimecode`), so it keeps its explanation.
  // An expired URL retries instead, so it is neither disabled nor a fallback.
  const retries = retriesOnActivate(player)
  const opensFallback =
    !availability.ready && !availability.loading && !retries && onUnavailable !== undefined

  const button = (
    <button
      type="button"
      aria-disabled={availability.ready || retries || opensFallback ? undefined : true}
      aria-describedby={availability.ready || retries || opensFallback ? undefined : reasonId}
      aria-label={
        opensFallback
          ? t('player.openTranscriptAt', { time })
          : t('transcript.line.playFrom', { time })
      }
      onClick={() => activateTimecode(player, seconds, onUnavailable)}
      className={cn(
        BASE,
        'transition-colors',
        availability.ready || retries || opensFallback
          ? 'hover:text-brand hover:underline'
          : 'cursor-not-allowed opacity-60 decoration-dotted underline',
        className,
      )}
    >
      {time}
    </button>
  )

  // ALWAYS THE SAME TREE. Returning a bare button in one state and a
  // Tooltip-wrapped one in another changes the button's parent, so React
  // remounts it on every ready <-> not-ready change and keyboard focus is lost.
  // With no reason the Tooltip gets null content and renders no bubble.
  const reason =
    availability.ready || retries || opensFallback || availability.reasonKey === null
      ? null
      : t(availability.reasonKey)
  return (
    <>
      <Tooltip content={reason}>{button}</Tooltip>
      {reason !== null && (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      )}
    </>
  )
}
